import React, { useEffect, useState } from 'react';
import { ApiDocumentType, getDocumentTypes, addDocumentType, updateDocumentType, deleteDocumentType, getUserGroups, ApiUserGroup, getDocumentTypePermissions, ApiPermission, addPermission, deletePermission } from '@api';
import { ApiPermissionTemplate, getPermissionTemplates, createPermissionTemplate } from '@api/permissionTemplates';

const OBJECTTYPE_DOCUMENTTYPE = 2; // backend enum
interface DtForm { documentTypeName: string; documentTypeDescription: string; documentTypeExtension: string; documentTypeOCR: boolean; }
const emptyDt: DtForm = { documentTypeName: '', documentTypeDescription: '', documentTypeExtension: '', documentTypeOCR: false };

interface PermState { [groupId: number]: { full: boolean; templateId?: number; permissionId?: string; }; }

export default function DocumentTypeAdmin() {
  const [docTypes, setDocTypes] = useState<ApiDocumentType[]>([]);
  const [groups, setGroups] = useState<ApiUserGroup[]>([]);
  const [templates, setTemplates] = useState<ApiPermissionTemplate[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<DtForm>(emptyDt);
  const [permState, setPermState] = useState<PermState>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { init(); }, []);

  async function init() {
    setLoading(true); setError(null);
    try { const [dts, gs, tmpls] = await Promise.all([getDocumentTypes(), getUserGroups(), getPermissionTemplates()]); setDocTypes(dts); setGroups(gs); setTemplates(tmpls); } catch (e: any) { setError(e.message); } finally { setLoading(false); }
  }

  async function loadPermissions(id: number) {
    try {
      const perms = await getDocumentTypePermissions(id);
      const map: PermState = {};
      perms.forEach(p => {
        const tmpl = templates.find(t => t.id === p.permissionTemplateId);
        const full = !!(tmpl && (tmpl.fullControl ?? (tmpl as any).FullControl));
        map[p.groupId] = { full, templateId: p.permissionTemplateId, permissionId: p.id };
      });
      groups.forEach(g => { if (!map[g.id]) map[g.id] = { full: false }; });
      setPermState(map);
    } catch { setPermState({}); }
  }

  function handleSelect(id: number) {
    setSelectedId(id); setEditing(false);
    const dt = docTypes.find(d => d.id === id);
    setForm({
      documentTypeName: dt?.documentTypeName || '',
      documentTypeDescription: dt?.documentTypeDescription || '',
      documentTypeExtension: dt?.documentTypeExtension || '',
      documentTypeOCR: !!(dt?.documentTypeOCR ?? (dt as any)?.DocumentTypeOCR)
    });
    loadPermissions(id);
  }
  function handleAdd() { setSelectedId(null); setEditing(true); setForm(emptyDt); setPermState({}); }
  function handleEdit() { if (selectedId != null) setEditing(true); }

  async function handleDelete() {
    if (selectedId == null) return; if (!window.confirm('Delete this document type?')) return;
    setLoading(true); setError(null);
    try { await deleteDocumentType(selectedId); await init(); setSelectedId(null); } catch (e: any) { setError(e.message); } finally { setLoading(false); }
  }

  async function handleSave() {
    setLoading(true); setError(null);
    try {
      const payload = {
        documentTypeName: form.documentTypeName,
        documentTypeDescription: form.documentTypeDescription,
        documentTypeExtension: form.documentTypeExtension,
        documentTypeOCR: !!form.documentTypeOCR
      } as Omit<ApiDocumentType, 'id'>;
      let id = selectedId;
      if (id == null) { const nd = await addDocumentType(payload); id = nd.id; }
      else { await updateDocumentType(id, payload); }
      await init(); setSelectedId(id!); setEditing(false); await loadPermissions(id!);
    } catch (e: any) { setError(e.message); } finally { setLoading(false); }
  }

  function onFormChange(e: React.ChangeEvent<HTMLInputElement>) {
    const { name, value, type, checked } = e.target;
    setForm((p: DtForm) => ({
      ...p,
      [name]: type === 'checkbox' ? checked : value
    }));
  }
  function toggleFull(groupId: number) { setPermState(prev => { const clone = { ...prev }; if (!clone[groupId]) clone[groupId] = { full: false }; clone[groupId].full = !clone[groupId].full; return clone; }); }

  async function ensureFullTemplate(): Promise<ApiPermissionTemplate> {
    const fullExisting = templates.find(t => (t.fullControl ?? (t as any).FullControl) === true);
    if (fullExisting) return fullExisting;
    const tmpl = await createPermissionTemplate({ fullControl: true, canRead: true, canWrite: true, canModify: true, canDelete: true, permissionLabel: 'Full Control' });
    setTemplates((prev: ApiPermissionTemplate[]) => [...prev, tmpl]);
    return tmpl;
  }

  async function savePermissions() {
    if (selectedId == null) return;
    setLoading(true); setError(null);
    try {
      const currentPerms = await getDocumentTypePermissions(selectedId);
      for (const g of groups) {
        const st = permState[g.id]; const existing = currentPerms.find(p => p.groupId === g.id && p.objectId === selectedId && p.objectTypeId === OBJECTTYPE_DOCUMENTTYPE);
        if (!st || !st.full) { if (existing) await deletePermission(existing.id); continue; }
        const tmpl = await ensureFullTemplate();
        if (!existing) {
          await addPermission({ groupId: g.id, objectId: selectedId, objectTypeId: OBJECTTYPE_DOCUMENTTYPE, permissionTemplateId: tmpl.id });
        } else if (existing.permissionTemplateId !== tmpl.id) {
          await deletePermission(existing.id);
          await addPermission({ groupId: g.id, objectId: selectedId, objectTypeId: OBJECTTYPE_DOCUMENTTYPE, permissionTemplateId: tmpl.id });
        }
      }
      await loadPermissions(selectedId);
    } catch (e: any) { setError(e.message); } finally { setLoading(false); }
  }

  const inputStyle: React.CSSProperties = { padding: '4px 6px', fontSize: 14, borderRadius: 4, border: '1px solid #cfd8dc', background: '#f7fafd', width: '100%' };
  const buttonPrimary: React.CSSProperties = { padding: '8px 16px', borderRadius: 6, background: '#4e6e8e', color: '#fff', fontWeight: 600, border: 'none', cursor: 'pointer' };
  const buttonSecondary: React.CSSProperties = { ...buttonPrimary, background: '#e0e6ed', color: '#123', fontWeight: 500 };
  const buttonDanger: React.CSSProperties = { ...buttonPrimary, background: '#c62828' };

  return (
      <div style={{ display: 'flex', height: 'calc(90vh / var(--ui-scale))', border: '1px solid #ccc', borderRadius: 12, overflow: 'hidden' }}>
      <div style={{ width: 380, borderRight: '1px solid #ccc', display: 'flex', flexDirection: 'column', background: '#f7fafd' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 16px', borderBottom: '1px solid #e0e6ed' }}>
          <h3 style={{ margin: 0, fontWeight: 600 }}>Document Types</h3>
          <button onClick={handleAdd} title='Add document type' style={{ background: '#4e6e8e', border: 'none', width: 36, height: 36, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
            <svg width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='#fff' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'><line x1='12' y1='5' x2='12' y2='19' /><line x1='5' y1='12' x2='19' y2='12' /></svg>
          </button>
        </div>
        {loading && <div style={{ padding: '8px 16px' }}>Loading...</div>}
        {error && <div style={{ padding: '8px 16px', color: 'red' }}>{error}</div>}
        <div style={{ overflowY: 'auto', flex: 1 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ background: '#eaf1f7' }}>
                <th style={{ textAlign: 'left', padding: '6px 10px', borderBottom: '1px solid #e0e6ed' }}>Name</th>
                <th style={{ textAlign: 'left', padding: '6px 10px', borderBottom: '1px solid #e0e6ed', width: 90 }}>Ext</th>
                <th style={{ textAlign: 'center', padding: '6px 10px', borderBottom: '1px solid #e0e6ed', width: 60 }}>OCR</th>
              </tr>
            </thead>
            <tbody>
              {docTypes.map(dt => { const sel = dt.id === selectedId; return (
                <tr key={dt.id} onClick={() => handleSelect(dt.id)} style={{ cursor: 'pointer', background: sel ? '#d8ecff' : 'transparent' }}>
                  <td style={{ padding: '6px 10px', borderBottom: '1px solid #f0f4f7' }}>{dt.documentTypeName}</td>
                  <td style={{ padding: '6px 10px', borderBottom: '1px solid #f0f4f7' }}>{dt.documentTypeExtension || ''}</td>
                  <td style={{ padding: '6px 10px', borderBottom: '1px solid #f0f4f7', textAlign: 'center' }}>{(dt.documentTypeOCR ?? (dt as any).DocumentTypeOCR) ? '✓' : ''}</td>
                </tr> ); })}
              {docTypes.length === 0 && !loading && (<tr><td colSpan={3} style={{ padding: 16, textAlign: 'center', color: '#888' }}>No document types</td></tr>)}
            </tbody>
          </table>
        </div>
      </div>

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '16px 24px' }}>
        {editing ? (
          <div style={{ maxWidth: 700 }}>
            <h3 style={{ marginTop: 0 }}>{selectedId == null ? 'Add Document Type' : 'Edit Document Type'}</h3>
            <form onSubmit={e => { e.preventDefault(); handleSave(); }} style={{ display: 'grid', gridTemplateColumns: '160px 1fr', rowGap: 12, columnGap: 16, background: '#fff', padding: 20, borderRadius: 12, border: '1px solid #e0e6ed', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
              <label style={{ alignSelf: 'center', fontWeight: 500 }}>Name</label>
              <input name='documentTypeName' value={form.documentTypeName} onChange={onFormChange} required style={inputStyle} />
              <label style={{ alignSelf: 'center', fontWeight: 500 }}>Description</label>
              <input name='documentTypeDescription' value={form.documentTypeDescription} onChange={onFormChange} style={inputStyle} />
              <label style={{ alignSelf: 'center', fontWeight: 500 }}>Extension</label>
              <input name='documentTypeExtension' value={form.documentTypeExtension} onChange={onFormChange} required style={inputStyle} />
              <label style={{ alignSelf: 'center', fontWeight: 500 }}>OCR</label>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <input name='documentTypeOCR' type='checkbox' checked={!!form.documentTypeOCR} onChange={onFormChange} />
              </div>
              <div style={{ gridColumn: '1 / span 2', display: 'flex', gap: 12, marginTop: 8 }}>
                <button type='submit' style={buttonPrimary} disabled={loading}>Save</button>
                <button type='button' style={buttonSecondary} onClick={() => setEditing(false)} disabled={loading}>Cancel</button>
              </div>
            </form>
          </div>
        ) : selectedId != null ? (
          <div style={{ maxWidth: 1400 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <h3 style={{ margin: 0 }}>Document Type Details</h3>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={handleEdit} style={buttonSecondary} disabled={loading}>Edit</button>
                <button onClick={handleDelete} style={buttonDanger} disabled={loading}>Delete</button>
              </div>
            </div>
            <div style={{ background: '#fff', padding: 16, borderRadius: 12, border: '1px solid #e0e6ed', boxShadow: '0 1px 4px rgba(0,0,0,0.04)', marginBottom: 20, display: 'grid', gridTemplateColumns: '160px 1fr', rowGap: 6 }}>
              <div style={{ fontWeight: 500 }}>Name:</div><div>{form.documentTypeName}</div>
              <div style={{ fontWeight: 500 }}>Description:</div><div>{form.documentTypeDescription}</div>
              <div style={{ fontWeight: 500 }}>Extension:</div><div>{form.documentTypeExtension}</div>
              <div style={{ fontWeight: 500 }}>OCR:</div><div>{form.documentTypeOCR ? 'Yes' : 'No'}</div>
            </div>

            <div style={{ background: '#fff', padding: 16, borderRadius: 12, border: '1px solid #e0e6ed', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
              <h4 style={{ margin: '0 0 12px 0' }}>Permissions (Full Control only)</h4>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ borderCollapse: 'collapse', minWidth: 600, fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: '#eaf1f7' }}>
                      <th style={{ textAlign: 'left', padding: '6px 10px', borderBottom: '1px solid #e0e6ed' }}>Group</th>
                      <th style={{ padding: '6px 10px', borderBottom: '1px solid #e0e6ed' }}>Full</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groups.map(g => { const st = permState[g.id] || { full: false }; return (
                      <tr key={g.id}>
                        <td style={{ padding: '6px 10px', borderBottom: '1px solid #f0f4f7', textAlign: 'left' }}>{g.groupName}</td>
                        <td style={{ textAlign: 'center', borderBottom: '1px solid #f0f4f7' }}><input type='checkbox' checked={st.full} onChange={() => toggleFull(g.id)} /></td>
                      </tr> ); })}
                  </tbody>
                </table>
              </div>
              <div style={{ marginTop: 12, display: 'flex', gap: 12 }}>
                <button onClick={savePermissions} style={buttonPrimary} disabled={loading}>Save Permissions</button>
                <button onClick={() => loadPermissions(selectedId!)} style={buttonSecondary} disabled={loading}>Reset</button>
              </div>
            </div>
          </div>
        ) : (
          <div style={{ color: '#567', fontSize: 16, paddingTop: 12 }}>Select a document type or click the + icon to add a new one.</div>
        )}
      </div>
    </div>
  );
}
